// Songs written for scenes (docs/scene-audio.md, "Generated songs"). A song plan is the single source for three
// things: the music model's prompt (sections with timestamps derived from bars and tempo), the scene's sung lines
// (each lyric line is a beat line for its vocalist), and the lyrics the recording is aligned to afterwards.
// Character names never reach the music model: vocalists are addressed by a role label ("RABBIT", "TEACHER").
import { z } from "zod";
import { MAX_SCENE_AUDIO_MS, MAX_SCENE_AUDIO_SINGERS, zSceneAudioMode } from "./sceneAudio";

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
const text = (max: number) => z.string().trim().max(max);

/** sung melody, rap, rhythmic speech, a group chant, or no voice at all. */
export const SONG_DELIVERIES = ["sung", "rap", "spoken", "chant", "instrumental"] as const;
export const zSongDelivery = z.enum(SONG_DELIVERIES);
export type SongDelivery = z.infer<typeof zSongDelivery>;

export const zSongVocalist = z
  .object({
    label: z.enum(["A", "B"]),
    /** The cast role that performs this voice, when the song belongs to a story. */
    characterId: id.optional(),
    /** The character's name, for people. Never sent to the music model. */
    name: text(80),
    /** What the music model calls this voice: one or two plain uppercase words, never a name ("RABBIT"). */
    role: z
      .string()
      .trim()
      .regex(/^[A-Z][A-Z ]{0,23}$/),
    /** Register, timbre, accent-free delivery and character, in plain musical terms. */
    voice: text(500).min(1),
    /** A few words the music model hears at every section this voice performs: gender, register, delivery ("MALE TENOR POP"). */
    tag: text(60).optional(),
  })
  .strict();
export type SongVocalist = z.infer<typeof zSongVocalist>;

export const zSongSection = z
  .object({
    id,
    /** Short heading, e.g. "Front teeth" or "Surprise rap". */
    label: text(40).min(1),
    vocalist: z.enum(["A", "B", "both", "none"]),
    delivery: zSongDelivery,
    bars: z.number().int().min(1).max(64),
    /** A sound event exactly where the section starts ("Record scratch, then a heavy bass drop"). */
    entrance: text(120).optional(),
    /** Musical direction for this section: instrumentation, energy, transitions in and out. */
    arrangement: text(400).optional(),
    /** The exact words, one performed line per entry. Empty for instrumental sections. */
    lines: z.array(text(200).min(1)).max(24),
    /** Music-video staging for this section (who appears, what they do, how the visuals escalate). Never sent to the music model. */
    visual: text(600).optional(),
  })
  .strict();
export type SongSection = z.infer<typeof zSongSection>;

export const zSongStyle = z
  .object({
    /** Genre, instrumentation, mood and production, in plain musical terms with no artist or work names. */
    description: text(600).min(1),
    bpm: z.number().int().min(60).max(200),
    key: text(30).optional(),
    meter: z.enum(["4/4", "3/4", "6/8"]).default("4/4"),
  })
  .strict();
export type SongStyle = z.infer<typeof zSongStyle>;

export const zSongPlan = z
  .object({
    version: z.literal(1).default(1),
    title: text(80).min(1),
    /** One sentence: what the song is and why it is fun. */
    premise: text(300).min(1),
    mode: zSceneAudioMode,
    language: text(30).default("English"),
    style: zSongStyle,
    vocalists: z.array(zSongVocalist).max(MAX_SCENE_AUDIO_SINGERS),
    sections: z.array(zSongSection).min(1).max(24),
    /** Performance notes for the whole song (transitions, backing vocals, what must stay clear). */
    notes: z.array(text(300).min(1)).max(12).default([]),
    /** How the song ends, e.g. "Final musical hit" or "Big group shout, then a cymbal swell". */
    ending: text(200).default("Final musical hit"),
  })
  .strict()
  .superRefine((plan, ctx) => {
    const labels = new Set(plan.vocalists.map((v) => v.label));
    if (labels.size !== plan.vocalists.length)
      ctx.addIssue({ code: "custom", message: "Each vocalist needs its own label." });
    const roles = new Set(plan.vocalists.map((v) => v.role));
    if (roles.size !== plan.vocalists.length)
      ctx.addIssue({ code: "custom", message: "Each vocalist needs its own role label." });
    plan.sections.forEach((section, i) => {
      const singers = section.vocalist === "both" ? ["A", "B"] : section.vocalist === "none" ? [] : [section.vocalist];
      for (const label of singers)
        if (!labels.has(label as "A" | "B"))
          ctx.addIssue({ code: "custom", path: ["sections", i, "vocalist"], message: `No vocalist ${label}.` });
      if ((section.vocalist === "none") !== (section.delivery === "instrumental"))
        ctx.addIssue({
          code: "custom",
          path: ["sections", i, "delivery"],
          message: "Only a section without a vocalist is instrumental.",
        });
      if (section.delivery === "instrumental" && section.lines.length)
        ctx.addIssue({ code: "custom", path: ["sections", i, "lines"], message: "An instrumental has no lines." });
      if (section.delivery !== "instrumental" && !section.lines.length)
        ctx.addIssue({ code: "custom", path: ["sections", i, "lines"], message: "A vocal section needs lines." });
    });
    if (new Set(plan.sections.map((s) => s.id)).size !== plan.sections.length)
      ctx.addIssue({ code: "custom", message: "Section ids must be unique." });
    if (songDurationMs(plan) > MAX_SCENE_AUDIO_MS)
      ctx.addIssue({ code: "custom", message: "The song is longer than ten minutes." });
  });
export type SongPlan = z.infer<typeof zSongPlan>;
export type SongPlanInput = z.input<typeof zSongPlan>;

const BEATS_PER_BAR = { "4/4": 4, "3/4": 3, "6/8": 2 } as const;

/** Milliseconds per bar at the plan's tempo (6/8 counts two dotted-quarter beats per bar). */
export function songBarMs(style: Pick<SongStyle, "bpm" | "meter">): number {
  return (BEATS_PER_BAR[style.meter ?? "4/4"] * 60_000) / style.bpm;
}

/** Each section's planned start and end on the song's timeline, in order. */
export function songSectionTimes(
  plan: Pick<SongPlan, "style" | "sections">
): { id: string; startMs: number; endMs: number }[] {
  const bar = songBarMs(plan.style);
  let bars = 0;
  return plan.sections.map((section) => {
    const startMs = Math.round(bars * bar);
    bars += section.bars;
    return { id: section.id, startMs, endMs: Math.round(bars * bar) };
  });
}

export function songDurationMs(plan: Pick<SongPlan, "style" | "sections">): number {
  const sections = songSectionTimes(plan);
  return sections[sections.length - 1]?.endMs ?? 0;
}

/** A performed line with the vocalist the plan gives it. */
export interface SongLine {
  sectionId: string;
  text: string;
  vocalist: "A" | "B" | "both";
  delivery: SongDelivery;
  /** The plan's estimate of where the line falls: the section's span split evenly by line. */
  plannedStartMs: number;
  plannedEndMs: number;
}

/** Every performed line in order, with the planned (not measured) time it falls in. */
export function songLines(plan: Pick<SongPlan, "style" | "sections">): SongLine[] {
  const times = songSectionTimes(plan);
  return plan.sections.flatMap((section, i) => {
    if (section.vocalist === "none") return [];
    const { startMs, endMs } = times[i];
    const step = (endMs - startMs) / Math.max(1, section.lines.length);
    return section.lines.map((line, n) => ({
      sectionId: section.id,
      text: line,
      vocalist: section.vocalist as "A" | "B" | "both",
      delivery: section.delivery,
      plannedStartMs: Math.round(startMs + n * step),
      plannedEndMs: Math.round(startMs + (n + 1) * step),
    }));
  });
}

/** Music styles a person can pick without describing one. Descriptions name no artists or works. */
export const SONG_STYLE_PRESETS = [
  {
    id: "bright-pop",
    label: "Bright pop",
    description:
      "Bright, bouncy children's pop with a bouncy bass, sparkling synths, handclaps and punchy drums; big, simple, singable hooks.",
    bpm: 120,
  },
  {
    id: "pop-rap",
    label: "Pop + rap",
    description:
      "Bright sung pop choruses that switch to hard-hitting hip-hop verses: chunky kick and snare, record scratches and heavy bass drops under the rap.",
    bpm: 120,
  },
  {
    id: "broadway",
    label: "Stage musical",
    description:
      "A theatrical stage-musical number: orchestral pit band with brass stabs and strings, dramatic builds, a big belted finish.",
    bpm: 112,
  },
  {
    id: "storybook-waltz",
    label: "Storybook waltz",
    description:
      "A gentle, sparkling orchestral waltz with celesta, pizzicato strings and warm woodwinds, like a music box come to life.",
    bpm: 96,
  },
  {
    id: "calypso",
    label: "Island party",
    description:
      "A sunny calypso groove with steel drums, marimba, shakers and a bouncy bass; playful call-and-response vocals.",
    bpm: 110,
  },
  {
    id: "rock-anthem",
    label: "Rock anthem",
    description:
      "A fun, stomping rock anthem with crunchy guitars, gang vocals, stadium drums and a soaring chorus.",
    bpm: 128,
  },
  {
    id: "lullaby",
    label: "Lullaby",
    description: "A soft, slow lullaby with acoustic guitar, warm piano, gentle strings and a hushed, tender vocal.",
    bpm: 72,
  },
  {
    id: "disco",
    label: "Disco party",
    description:
      "A glittering disco-funk party: four-on-the-floor kick, slap bass, string swoops, horn hits and a falsetto-friendly hook.",
    bpm: 118,
  },
] as const;
export type SongStylePreset = (typeof SONG_STYLE_PRESETS)[number];
